import { useEffect, useState, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Users, MagnifyingGlass, ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { followsApi } from "@/lib/apiClient";
import FollowButton from "@/components/FollowButton";

const TIER_COLORS = {
    elite:    { bg: "rgba(212,175,55,0.18)",  color: "#D4AF37" },
    premium:  { bg: "rgba(147,112,219,0.18)", color: "#9370DB" },
    explorer: { bg: "rgba(64,196,255,0.15)",  color: "#40C4FF" },
    free:     { bg: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.5)" },
};

function tierStyle(tier) {
    return TIER_COLORS[tier?.toLowerCase()] || TIER_COLORS.free;
}

/**
 * FollowRosterModal
 * @param {string}   handle       - The profile handle whose roster to display
 * @param {"followers"|"following"} initialTab - Which tab to open first
 * @param {function} onClose      - Close callback
 */
export default function FollowRosterModal({ handle, initialTab = "followers", onClose }) {
    const [tab, setTab] = useState(initialTab);
    const [followers, setFollowers] = useState([]);
    const [following, setFollowing] = useState([]);
    const [loadingFollowers, setLoadingFollowers] = useState(false);
    const [loadingFollowing, setLoadingFollowing] = useState(false);
    const [query, setQuery] = useState("");

    // Fetch followers list
    useEffect(() => {
        if (!handle) return;
        setLoadingFollowers(true);
        followsApi.followers(handle)
            .then((r) => setFollowers(r.items || r.users || []))
            .catch(() => setFollowers([]))
            .finally(() => setLoadingFollowers(false));
    }, [handle]);

    // Fetch following list
    useEffect(() => {
        if (!handle) return;
        setLoadingFollowing(true);
        followsApi.following(handle)
            .then((r) => setFollowing(r.items || r.users || []))
            .catch(() => setFollowing([]))
            .finally(() => setLoadingFollowing(false));
    }, [handle]);

    // Close on Escape
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const activeList = tab === "followers" ? followers : following;
    const isLoading  = tab === "followers" ? loadingFollowers : loadingFollowing;

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return activeList;
        return activeList.filter(
            (u) =>
                u.handle?.toLowerCase().includes(q) ||
                u.name?.toLowerCase().includes(q)
        );
    }, [activeList, query]);

    const TABS = [
        { key: "followers", label: "Followers", count: followers.length },
        { key: "following", label: "Following", count: following.length },
    ];

    return (
        <AnimatePresence>
            <motion.div
                key="roster-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[80] flex items-center justify-center p-4"
                style={{ background: "rgba(3,7,15,0.82)", backdropFilter: "blur(6px)" }}
                onClick={onClose}
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.94, y: 24 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.94, y: 24 }}
                    transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full max-w-md rounded-3xl overflow-hidden flex flex-col"
                    style={{
                        background: "linear-gradient(160deg, rgba(18,26,42,0.98) 0%, rgba(11,19,32,0.99) 100%)",
                        border: "1px solid rgba(212,175,55,0.2)",
                        boxShadow: "0 24px 80px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.05)",
                        maxHeight: "85vh",
                    }}
                >
                    {/* Header */}
                    <div
                        className="flex items-center justify-between px-6 py-5 shrink-0"
                        style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
                    >
                        <div className="flex items-center gap-3">
                            <div
                                className="w-9 h-9 rounded-xl grid place-items-center"
                                style={{ background: "rgba(212,175,55,0.12)" }}
                            >
                                <Users size={18} color="#D4AF37" weight="fill" />
                            </div>
                            <div className="font-semibold text-white text-sm leading-tight">
                                @{handle}
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="w-8 h-8 rounded-full grid place-items-center transition-colors hover:bg-white/10"
                            data-testid="roster-modal-close"
                        >
                            <X size={16} color="rgba(255,255,255,0.5)" />
                        </button>
                    </div>

                    {/* Tabs */}
                    <div
                        className="flex shrink-0 px-6 pt-4 gap-1"
                    >
                        {TABS.map((t) => (
                            <button
                                key={t.key}
                                onClick={() => { setTab(t.key); setQuery(""); }}
                                className="flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all"
                                style={{
                                    background: tab === t.key ? "rgba(212,175,55,0.15)" : "transparent",
                                    color: tab === t.key ? "var(--gold)" : "rgba(255,255,255,0.4)",
                                    border: tab === t.key ? "1px solid rgba(212,175,55,0.3)" : "1px solid transparent",
                                }}
                                data-testid={`roster-tab-${t.key}`}
                            >
                                {t.label}
                                {!isLoading && (
                                    <span
                                        className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px]"
                                        style={{
                                            background: tab === t.key ? "rgba(212,175,55,0.2)" : "rgba(255,255,255,0.08)",
                                            color: tab === t.key ? "var(--gold)" : "rgba(255,255,255,0.4)",
                                        }}
                                    >
                                        {t.count}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Search */}
                    <div className="px-6 pt-4 pb-3 shrink-0">
                        <div
                            className="flex items-center gap-3 rounded-xl px-4 py-2.5"
                            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}
                        >
                            <MagnifyingGlass size={14} color="rgba(255,255,255,0.35)" />
                            <input
                                type="text"
                                placeholder="Search by name or handle…"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                className="flex-1 bg-transparent text-white text-sm outline-none placeholder:text-white/30"
                                data-testid="roster-search-input"
                            />
                        </div>
                    </div>

                    {/* User List */}
                    <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-2 min-h-0">
                        {isLoading ? (
                            <div className="text-white/40 text-sm text-center py-12">Loading…</div>
                        ) : filtered.length === 0 ? (
                            <div className="text-white/40 text-sm text-center py-12">
                                {query ? "No results match your search." : `No ${tab} yet.`}
                            </div>
                        ) : (
                            filtered.map((u) => {
                                const ts = tierStyle(u.tier);
                                return (
                                    <div
                                        key={u.handle}
                                        className="flex items-center gap-3 rounded-2xl p-3 group transition-colors hover:bg-white/[0.04]"
                                        data-testid={`roster-user-${u.handle}`}
                                    >
                                        {/* Avatar */}
                                        <div className="relative shrink-0">
                                            <img
                                                src={u.avatar_url || `https://api.dicebear.com/7.x/notionists/svg?seed=${u.handle}`}
                                                alt={u.handle}
                                                className="w-10 h-10 rounded-full object-cover"
                                                style={{ border: `1.5px solid ${ts.color}` }}
                                            />
                                        </div>

                                        {/* Info */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="text-white text-sm font-medium truncate">{u.name || u.handle}</span>
                                                {u.tier && (
                                                    <span
                                                        className="text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-full shrink-0"
                                                        style={{ background: ts.bg, color: ts.color }}
                                                    >
                                                        {u.tier}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-white/40 text-xs mt-0.5 truncate">@{u.handle}</div>
                                        </div>

                                        {/* Actions */}
                                        <div className="flex items-center gap-2 shrink-0">
                                            <FollowButton handle={u.handle} size="sm" />
                                            <Link
                                                to={`/profile/${u.handle}`}
                                                onClick={onClose}
                                                className="w-7 h-7 rounded-full grid place-items-center transition-all opacity-0 group-hover:opacity-100 hover:bg-white/10"
                                                data-testid={`roster-goto-${u.handle}`}
                                                title="View profile"
                                            >
                                                <ArrowRight size={13} color="rgba(255,255,255,0.5)" />
                                            </Link>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
